/**
 * Authentication store slice for user authentication state management
 * Replaces the existing AuthProvider context with Zustand store
 */

import { clearSentryUser, setSentryUser } from "@/lib/sentry";
import { authService } from "@/services";
import { Workspace } from "@/types";
import type { SessionFeatures } from "../types";
import { api } from "@/utils/api-client";
import { getUserRole } from "@/utils/auth-storage";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import type { User } from "../../lib/auth-provider";
import { devtoolsDev } from "../middleware";
import type { AuthStore } from "../types";
import { workspaceStore } from "./workspace-slice";

/**
 * Initial authentication state
 */
const initialAuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
  // Server-driven feature flags; populated from the /auth/session response.
  // Defaults closed so gated modules stay hidden until the server confirms —
  // except `codingHidden`, which defaults false because Coding is visible
  // unless a deployment opts out, and must not blink out before the session
  // response lands.
  features: { projectsEnabled: false, codingHidden: false },
};

/**
 * Creates the authentication store with actions
 */
const createAuthStore = () =>
  create<AuthStore>()(
    devtoolsDev(
      // @ts-ignore
      persist(
        immer((set, get) => ({
          // Initial state
          ...initialAuthState,

          // Actions
          // @ts-ignore
          login: async (email: string, password: string, invitationId?: string) => {
            // Prevent multiple simultaneous login attempts
            const currentState = get();
            if (currentState.isLoading) {
              throw new Error("Login already in progress");
            }

            set((state) => {
              state.isLoading = true;
              state.error = null;
            });

            try {
              // Actual API integration
              const result = await authService.signin({
                email: email,
                password: password,
                ...(invitationId && { invitationId }),
              });
              console.log("Login response:", result); // Debug log for API response

              // if the user it not verified, we do not set isAuthenticated to true
              // Will navigate to the verify-otp page instead
              if (
                result.redirectUrl &&
                result.redirectUrl.includes("/verify-otp")
              ) {
                set((state) => {
                  state.user = result.user || null;
                  state.isAuthenticated = false; // Not authenticated until verified
                  state.isLoading = false;
                  state.error = null;
                });
                return result;
              }

              // Update auth state with real API response data
              // Merge the workspace role (fetched during signin) into the user object
              const storedRole = getUserRole();
              set((state) => {
                state.user = result.user;
                if (state.user && storedRole) {
                  state.user.role = storedRole;
                }
                state.isAuthenticated = true;
                state.isLoading = false;
                state.error = null;
              });

              // Set Sentry user context for error attribution
              if (result.user?.id) {
                setSentryUser(result.user.id, result.user.email);
              }

              // // Save workspace data if available in the response
              if (result.selectedWorkspace || result.workspaces) {
                const workspaceActions = workspaceStore.getState();

                // Set workspaces if available
                if (result.workspaces && Array.isArray(result.workspaces)) {
                  // @ts-ignore
                  workspaceActions.setWorkspaces(
                    result.workspaces as unknown as Workspace[],
                  );
                }

                // Set current workspace if available
                if (result.selectedWorkspace) {
                  workspaceActions.setCurrentWorkspace(
                    result?.selectedWorkspace as any,
                  );
                }
              }

              return result;
            } catch (error) {
              const errorMessage =
                error instanceof Error ? error.message : "Login failed";
              set((state) => {
                state.user = null;
                state.isAuthenticated = false;
                state.isLoading = false;
                state.error = errorMessage;
              });
              throw error; // Re-throw the original error instead of creating a new one
            }
          },

          // Client state is torn down BEFORE the service call, not after it.
          // The old order awaited the network first, which left the signed-in
          // user, workspace and sidebar rendered for the length of the round
          // trip, and made the post-await cleanup race the hard navigation that
          // `authService.signout()` starts. Everything below is synchronous, so
          // by the time the navigation begins the store holds no user data.
          logout: async () => {
            // Clear Sentry user context on logout
            clearSentryUser();

            set((state) => {
              state.user = null;
              state.isAuthenticated = false;
              state.error = null;
              state.isLoading = false;
            });

            // Clear workspace data on logout
            const workspaceActions = workspaceStore.getState();
            workspaceActions.setCurrentWorkspace(null);
            workspaceActions.setWorkspaces([]);

            // Drop the persisted copy too. Must follow the `set` calls above:
            // the persist middleware writes on every state change, so clearing
            // first would just be overwritten by the signed-out snapshot.
            try {
              localStorage.removeItem("taqat-auth-store");
            } catch (error) {
              console.warn("Failed to clear auth storage:", error);
            }

            // Revokes the session server-side and redirects to /signin. Never
            // throws for a failed revocation — local credentials are gone
            // regardless — so there is nothing left here to recover from.
            await authService.signout();
          },

          register: async (
            name: string,
            email: string,
            password: string,
            invitationId?: string,
          ) => {
            set((state) => {
              state.isLoading = true;
              state.error = null;
            });

            try {
              // Call actual signup API
              const response = await authService.signup({
                name,
                email,
                password,
                confirmPassword: password,
                ...(invitationId && { invitationId }),
              });

              // Handle response based on verification status
              if (
                response.redirectUrl &&
                response.redirectUrl.includes("/verify-otp")
              ) {
                // User needs email verification
                set((state) => {
                  state.user = response.user || null;
                  state.isAuthenticated = false; // Not authenticated until verified
                  state.isLoading = false;
                  state.error = null;
                });

                // Return redirect URL for navigation
                return { redirectUrl: response.redirectUrl };
              } else if (response.session) {
                // Immediately authenticated — happens when a validated
                // invitation lets signup skip OTP (see signup.ts).
                set((state) => {
                  state.user = response.user || null;
                  state.isAuthenticated = true;
                  state.isLoading = false;
                  state.error = null;
                });

                // Save workspace data, matching what login() does — the
                // workspace was already attached server-side during signup.
                if (response.selectedWorkspace || response.workspaces) {
                  const workspaceActions = workspaceStore.getState();
                  if (response.workspaces && Array.isArray(response.workspaces)) {
                    // @ts-ignore
                    workspaceActions.setWorkspaces(
                      response.workspaces as unknown as Workspace[],
                    );
                  }
                  if (response.selectedWorkspace) {
                    workspaceActions.setCurrentWorkspace(
                      response.selectedWorkspace as any,
                    );
                  }
                }

                return {
                  redirectUrl: response.redirectUrl || "/app",
                };
              }

              return {};
            } catch (error) {
              const errorMessage =
                error instanceof Error ? error.message : "Registration failed";
              set((state) => {
                state.user = null;
                state.isAuthenticated = false;
                state.isLoading = false;
                state.error = errorMessage;
              });
              throw error;
            }
          },

          restoreSession: async () => {
            const currentState = get();
            if (currentState.isLoading) {
              return;
            }

            set((state) => {
              state.isLoading = true;
              state.error = null;
            });

            try {
              const response = await api.get("/auth/session", {
                requiresAuth: true,
                showErrorToast: false,
              });

              if (response.success && response.data) {
                set((state) => {
                  state.user = response.data.user;
                  state.isAuthenticated = true;
                  state.isLoading = false;
                  state.error = null;
                });

                // Set Sentry user context after session restore
                if (response.data.user?.id) {
                  setSentryUser(
                    response.data.user.id,
                    response.data.user.email,
                  );
                }

                // Save workspace data if available in the session response
                if (response.data.workspace || response.data.workspaces) {
                  const workspaceActions = workspaceStore.getState();

                  // Set workspaces if available
                  if (
                    response.data.workspaces &&
                    Array.isArray(response.data.workspaces)
                  ) {
                    workspaceActions.setWorkspaces(response.data.workspaces);
                  }

                  // Set current workspace if available
                  if (response.data.workspace) {
                    workspaceActions.setCurrentWorkspace(
                      response.data.workspace,
                    );
                  }
                }

                return response.data;
              }

              throw new Error("Session restoration failed");
            } catch (error) {
              // Don't set error for session restoration failures
              // This is an expected case (session expired) and shouldn't show as an error
              set((state) => {
                state.user = null;
                state.isAuthenticated = false;
                state.isLoading = false;
                state.error = null;
              });
              throw error;
            }
          },

          clearError: () => {
            set((state) => {
              state.error = null;
            });
          },

          setUser: (user: User) => {
            set((state) => {
              state.user = user;
              state.isAuthenticated = true;
              state.error = null;
            });
          },

          setFeatures: (features: SessionFeatures) => {
            set((state) => {
              state.features = features;
            });
          },

          setLoading: (loading: boolean) => {
            set((state) => {
              state.isLoading = loading;
            });
          },

          setError: (error: string | null) => {
            set((state) => {
              state.error = error;
            });
          },
        })),
        {
          name: "taqat-auth-store",
          storage: createJSONStorage(() => localStorage),
          partialize: (state) => ({
            // Persist authentication state and user data
            // This prevents unnecessary session API calls on page reload
            isAuthenticated: state.isAuthenticated,
            user: state.user,
            // Persist server-driven feature flags so gated modules don't flash
            // hidden on reload before the session re-validates.
            features: state.features,
            // Note: Tokens are handled separately by auth-storage.ts
            // We persist user data here to avoid re-fetching on every page load
          }),
        },
      ),
      "AuthStore",
    ),
  );

/**
 * Authentication store instance
 */
export const authStore = createAuthStore();

/**
 * Authentication store hook for components
 * @returns Authentication store state and actions
 */
export const useAuthStore = () => authStore();

/**
 * Simplified hook that mimics the original useAuth hook
 * Provides backward compatibility with existing components
 * @returns Authentication context-like interface
 */
export const useAuth = () => {
  const { user, isAuthenticated, login, logout, register, restoreSession } =
    useAuthStore();

  return {
    user,
    isAuthenticated,
    login,
    logout,
    register,
    restoreSession,
  };
};

/**
 * Hook for authentication loading state
 * @returns Loading state and error
 */
export const useAuthStatus = () => {
  const { isLoading, error, clearError } = useAuthStore();

  return {
    isLoading,
    error,
    clearError,
  };
};

/**
 * Hook for authentication actions only
 * @returns Authentication actions
 */
export const useAuthActions = () => {
  const {
    login,
    logout,
    register,
    restoreSession,
    clearError,
    setUser,
    setLoading,
    setError,
  } = useAuthStore();

  return {
    login,
    logout,
    register,
    restoreSession,
    clearError,
    setUser,
    setLoading,
    setError,
  };
};

/**
 * Selector hook for user data only
 * @returns Current user or null
 */
export const useCurrentUser = () => {
  return authStore((state) => state.user);
};

/**
 * Selector hook for authentication status only
 * @returns Boolean indicating if user is authenticated
 */
export const useIsAuthenticated = () => {
  return authStore((state) => state.isAuthenticated);
};
